import {
  accentOnWhite,
  contrastRatio,
  CORNER_PX,
  DEFAULT_CORNER_PX,
  readableInkOn,
  SYSTEM_FONT,
} from '@destera/core';

/**
 * Widget teması tek kaynaktan türer: sitenin marka rengi (data-accent). Balon, sohbet penceresi
 * ve açılır pencere yüzeyi aynı değişkenleri okur (DRY):
 *
 *   --d-accent  marka rengi
 *   --d-on      marka rengi ÜSTÜNDE okunur yazı/simge rengi
 *   --d-ink     marka renginin BEYAZ ÜSTÜNDE okunur hâli (bağlantı, etiket)
 *   --d-grad    marka yüzeyi: tarayıcı destekliyorsa marka renginden komşu tona geçiş, yoksa düz
 *   --d-r       pencerenin köşe yuvarlaklığı (§3.7.3 "Stil")
 *   --d-sh      yükseklik gölgesi; ölçeği ÇAĞIRAN veriyor (aşağıda)
 *   --d-ff      yazı tipi yığını ya da `inherit`
 *
 * **Karşıtlık hesabı ÇEKİRDEKTE** (`@destera/core`) ve bu zorunlu: panel de aynı sayıyı
 * gösteriyor. İki ayrı uygulama, işletmeye "bu renk okunur" deyip ziyaretçiye okunmaz bir yazı
 * göstermek demekti — ve bunu kimse fark etmezdi.
 */
export { accentOnWhite, readableInkOn } from '@destera/core';

/** Yazı renginin marka rengiyle karşıtlığı; denemeler eşiği bununla ölçüyor. */
export const contrastOn = (accent: string, ink: string): number => contrastRatio(accent, ink);

/**
 * Köşe ve yazı tipi ölçekleri ÇEKİRDEKTE (`@destera/core/style-tokens`).
 *
 * Panelin önizlemesi aynı sayıları çiziyor ve iki kopya, panelde 16 piksel gösterip
 * ziyaretçiye 24 piksel çizmek demekti — karşıtlık hesabının çekirdeğe taşınma gerekçesinin
 * aynısı. GÖLGE ölçeği burada kalıyor: değeri tarayıcıya özgü bir CSS dizesi ve iki pencere
 * için iki ayrı ölçek var (aşağıda).
 */

/**
 * Yükseklik gölgesi ölçeği; ad → CSS değeri.
 *
 * **İki ölçek var ve ÇAĞIRAN hangisini kullandığını söylüyor** (`themeCss`in ikinci bağımsız
 * değişkeni). Sebebi bütçe: yüzey penceresinin ve sohbet penceresinin bugünkü gölgeleri ayrı
 * değerler ve iki tabloyu da bu dosyada tutmak, İKİ ayrı paketin (yükleyici + yüzey) her birine
 * kullanmadığı tabloyu da indirtmek olurdu. Tabloyu dışarıdan almak, kullanılmayanı ağaç
 * sarsmasıyla düşürüyor.
 *
 * Tek bir gölgeye indirmek de seçenek değildi: pencerelerden birinin görünümünü haber vermeden
 * değiştirmek olurdu.
 */
export type ShadowScale = Record<string, string>;

/** YÜZEY penceresinin gölgeleri; `soft` bugünkü değer. */
export const SURFACE_SHADOWS: ShadowScale = {
  none: 'none',
  soft: '0 12px 40px rgb(0 0 0/.18)',
  strong: '0 18px 60px rgb(0 0 0/.3)',
};

/** SOHBET penceresinin gölgeleri; `soft` bugünkü değer. */
export const WINDOW_SHADOWS: ShadowScale = {
  none: 'none',
  soft: '0 28px 70px -18px rgb(17 24 39/.45)',
  strong: '0 36px 90px -16px rgb(17 24 39/.6)',
};

/** Markanın CSS'e giren parçası; adlar sunucudan geliyor, piksel karşılıkları burada. */
export type WidgetTheme = {
  readonly accent: string;
  readonly corner?: string | undefined;
  readonly shadow?: string | undefined;
  readonly font?: string | undefined;
};

/** Markanın tokenları; `config.ts`teki `WidgetBrand`in bu üç alanı. */
export type WidgetBrandTokens = {
  readonly corner?: string | undefined;
  readonly shadow?: string | undefined;
  readonly font?: string | undefined;
};

/**
 * Yapılandırma + marka → tema.
 *
 * **Renk iki yerden gelebiliyor, tokenlar YALNIZ markadan.** Renk gömme koduna yazılabiliyor
 * (`data-accent`) çünkü ilk karede doğru olması isteniyor; tokenları da oraya taşımak, köşesini
 * değiştiren işletmeden gömme kodunu yeniden yapıştırmasını istemek olurdu — ve o hata zaten bir
 * kez yapılmış, rengin kendisinde.
 *
 * Alanlar KOŞULSUZ yazılıyor (`?.` ile): tanınmayan ya da olmayan değer tabloda varsayılana
 * düşüyor, yani koşullu yayma dört satır bayt karşılığında hiçbir şey kazandırmazdı.
 */
export const themeOf = (accent: string, brand?: WidgetBrandTokens): WidgetTheme => ({
  accent,
  corner: brand?.corner,
  shadow: brand?.shadow,
  font: brand?.font,
});

/**
 * Gölge kökünün tema değişkenleri. Renk doğrulandığı için CSS'e güvenle yazılıyor.
 *
 * `--d-ink` AYRI bir değişken ve gerekliydi: marka rengi çoğu zaman arka plan ama bağlantılarda
 * yazı rengi olarak da kullanılıyor; açık bir marka rengini (sarı) oraya ham hâliyle yazmak,
 * beyaz üstünde okunmayan bir bağlantı üretmek olurdu.
 *
 * **`inherit` yazı tipinde `:host`a AYRICA `font-family:inherit` yazılıyor.** Gölge kökün `:host`
 * kuralı `all:initial` taşıyor ve o, sayfadan gelen yazı tipini de sıfırlıyor — yalnız değişkeni
 * `inherit` yapmak, sitenin yazı tipini değil tarayıcı varsayılanını devralmak olurdu.
 */
export function themeCss(theme: WidgetTheme, shadows: ShadowScale): string {
  const { accent } = theme;
  /** İki yerde okunuyor: değişkende ve `:host` geçersiz kılmasında. */
  const inherit = theme.font === 'inherit';
  return (
    `:host{--d-accent:${accent};--d-on:${readableInkOn(accent)};` +
    `--d-ink:${accentOnWhite(accent)};--d-grad:var(--d-accent);` +
    `--d-r:${String(CORNER_PX[theme.corner ?? ''] ?? DEFAULT_CORNER_PX)}px;` +
    `--d-sh:${shadows[theme.shadow ?? ''] ?? shadows['soft'] ?? 'none'};` +
    `--d-ff:${inherit ? 'inherit' : SYSTEM_FONT}` +
    `${inherit ? ';font-family:inherit' : ''}}` +
    '@supports (color:oklch(from red l c h)){:host{--d-grad:linear-gradient(135deg,var(--d-accent) 15%,oklch(from var(--d-accent) l c calc(h + 40)))}}'
  );
}
